import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '@/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import RestrictionModal from '@/components/RestrictionModal';
import useLearningRestrictions from '@/hooks/useLearningRestrictions';
import { REQUIRED_EXAM_COURSE } from '@/hooks/useCourseAccess';

interface Question {
  id: string;
  Question: string;
  Answer01: string;
  Answer02: string;
  Answer03: string;
  CollectAnswer?: string;
}

interface Shikaku {
  id: string;
  orderId: string;
  name: string;
}

export default function MockExamPlayPage() {
  const navigate = useNavigate();
  const { userId } = useAuth();

  // Selection State
  const [shikakus, setShikakus] = useState<Shikaku[]>([]);
  const [loadingShikakus, setLoadingShikakus] = useState(true);
  const [selectedShikakuId, setSelectedShikakuId] = useState<string>('');

  // Exam State
  const [currentPage, setCurrentPage] = useState(1);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<{ [key: string]: number }>({});
  const [loading, setLoading] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Result State
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<any>(null);
  const { settings, loading: restrictionsLoading } = useLearningRestrictions();
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

  useEffect(() => {
    if (!restrictionsLoading && !settings.mockExamEnabled) {
      setIsRestrictionOpen(true);
    }
  }, [restrictionsLoading, settings.mockExamEnabled]);

  useEffect(() => {
    if (submitted && result) {
      window.scrollTo(0, 0);
    }
  }, [submitted, result]);

  // Fetch Shikakus (Active Orders)
  useEffect(() => {
    const fetchShikakus = async () => {
      if (restrictionsLoading || !settings.mockExamEnabled) {
        setLoadingShikakus(false);
        return;
      }
      try {
        if (!userId) {
          setLoadingShikakus(false);
          return;
        }
        const userRef = doc(db, 'User', userId);
        const ordersQuery = query(
          collection(db, 'Order'),
          where('UserRef', '==', userRef),
          where('status', '==', 'active')
        );

        const snapshot = await getDocs(ordersQuery);
        const orderDocs = snapshot.docs;

        if (orderDocs.length === 0) {
          setShikakus([]);
          setLoadingShikakus(false);
          return;
        }

        const shikakulData = await Promise.all(
          orderDocs.map(async (orderDoc: any) => {
            const orderData = orderDoc.data();

            if (
              typeof orderData.course !== 'string' ||
              orderData.course.trim() !== REQUIRED_EXAM_COURSE
            ) {
              return null;
            }

            const shikakuRef = orderData.shikakuId;
            if (!shikakuRef) return null;

            const shikakuDoc = await getDoc(shikakuRef);
            if (!shikakuDoc.exists()) return null;

            const shikakuData = shikakuDoc.data() as any;
            return {
              id: shikakuDoc.id,
              orderId: orderDoc.id,
              name: shikakuData.ShikakuName || shikakuDoc.id
            };
          })
        );

        const validShikakus = shikakulData.filter(s => s !== null) as Shikaku[];
        setShikakus(validShikakus);
      } catch (error) {
        console.error('Error fetching shikakus:', error);
      } finally {
        setLoadingShikakus(false);
      }
    };

    fetchShikakus();
  }, [userId, restrictionsLoading, settings.mockExamEnabled]);

  // Start Mock Exam when qualification is selected
  useEffect(() => {
    if (restrictionsLoading || !settings.mockExamEnabled) return;
    if (!selectedShikakuId) return;

    const startMockExam = async () => {
      setLoading(true);
      setCurrentPage(1);
      setAnswers({});
      setSubmitted(false);
      setResult(null);

      try {
        const getMockFunc = httpsCallable(functions, 'getMockExamQuestions');
        const result = await getMockFunc({ shikakuId: selectedShikakuId });

        const data = result.data as any;
        if (data.success) {
          setQuestions(data.questions);
        } else {
          alert('問題の取得に失敗しました');
          setSelectedShikakuId('');
        }
      } catch (error: any) {
        console.error("Error fetching mock exam:", error);
        alert(`エラーが発生しました: ${error.message}`);
        setSelectedShikakuId('');
      } finally {
        setLoading(false);
      }
    };

    startMockExam();
  }, [selectedShikakuId, restrictionsLoading, settings.mockExamEnabled]);

  const questionsPerPage = 10;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = questions.slice(startIndex, endIndex);

  const answeredCount = Object.keys(answers).length;

  // Handle Answer Selection
  const handleAnswer = (questionId: string, optionIndex: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const answersArray = Object.entries(answers).map(([qId, optionIdx]) => {
      const question = questions.find(q => q.id === qId);
      if (!question) return null;

      const options = [question.Answer01, question.Answer02, question.Answer03].filter(a => a);
      const selectedAnswer = options[optionIdx];

      return {
        questionId: qId,
        selectedAnswer
      };
    }).filter(Boolean);

    try {
      const submitFunc = httpsCallable(functions, 'submitMockExam');
      const submitResult = await submitFunc({
        shikakuId: selectedShikakuId,
        answers: answersArray
      });

      const data = submitResult.data as any;
      setResult(data);
      setSubmitted(true);
      setShowSubmitForm(false);
    } catch (error: any) {
      console.error("Submission error:", error);
      alert(`送信エラー: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (restrictionsLoading) {
    return <div className="min-h-screen flex items-center justify-center">制限設定を確認中です...</div>;
  }

  if (!settings.mockExamEnabled) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <RestrictionModal
          open
          message={settings.message}
          actionLabel="学習メニューへ戻る"
          onAction={() => navigate('/learning')}
        />
      </div>
    );
  }

  if (loading || loadingShikakus) {
    return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
  }

  // --- Render Result View ---
  if (submitted && result) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
          <div className="max-w-md mx-auto px-4 h-14 flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 flex items-center justify-center -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <h1 className="flex-1 text-center font-bold text-gray-800 pr-8 text-base">
              模擬試験結果
            </h1>
          </div>
        </header>

        <div className="max-w-md mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">模擬試験結果</h2>
            <div className="text-5xl font-bold text-orange-500 mb-2">
              {result.score}点
            </div>
            <p className="text-gray-500">{result.score >= 80 ? '合格圏内です！' : 'もう少し頑張りましょう'}</p>
          </div>

          <div className="space-y-4 mb-8">
            {result.details.map((detail: any, index: number) => (
              <div key={index} className="bg-white rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xl font-bold ${detail.isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                    {detail.isCorrect ? '✓' : '✗'}
                  </span>
                  <span className="font-bold text-gray-700">Q{index + 1}</span>
                </div>
                <p className="text-gray-800 font-medium mb-3">{detail.question}</p>

                <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">あなたの回答:</span>
                    <span className="font-bold">{detail.userAnswer}</span>
                  </div>
                  {!detail.isCorrect && (
                    <div className="flex justify-between text-red-600">
                      <span>正解:</span>
                      <span className="font-bold">{detail.correctAnswer}</span>
                    </div>
                  )}
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  <strong>解説:</strong> {detail.explanation}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => {
                setSubmitted(false);
                setAnswers({});
                setResult(null);
                const currentId = selectedShikakuId;
                setSelectedShikakuId('');
                setTimeout(() => setSelectedShikakuId(currentId), 0);
              }}
              className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition"
            >
              もう一度挑戦
            </button>
            <button
              onClick={() => navigate('/learning')}
              className="flex-1 py-3 bg-gray-500 text-white rounded-xl font-bold hover:bg-gray-600 transition"
            >
              メニューに戻る
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Render Selection View ---
  if (!selectedShikakuId) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
          <div className="max-w-md mx-auto px-4 h-14 flex items-center">
            <button
              onClick={() => navigate('/mockexam')}
              className="w-10 h-10 flex items-center justify-center -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <h1 className="flex-1 text-center font-bold text-gray-800 pr-8 text-base">
              模擬試験 - 資格選択
            </h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6">
          <div className="space-y-4">
            {shikakus.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl text-center text-gray-500">
                受験可能な資格がありません
              </div>
            ) : (
              shikakus.map((shikaku) => (
                <button
                  key={shikaku.id}
                  onClick={() => setSelectedShikakuId(shikaku.id)}
                  className="w-full bg-white rounded-2xl shadow-sm p-5 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors border border-transparent hover:border-orange-200"
                >
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-500 text-xl">
                    <i className="ri-file-list-3-line"></i>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-base font-bold text-gray-800">{shikaku.name}</h2>
                    <p className="text-xs text-gray-500 mt-1">模擬試験を開始する</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-400 text-xl"></i>
                </button>
              ))
            )}
          </div>
        </main>
      </div>
    );
  }

  // --- Render Exam View ---
  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10 shadow-sm">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-500">
                <i className="ri-timer-flash-line text-xl"></i>
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-800">模擬試験</h1>
                <p className="text-xs text-gray-500">
                  {questions.length > 0 ? currentPage : 0}/{totalPages} ページ
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (window.confirm('模擬試験を中断しますか？')) {
                    setSelectedShikakuId('');
                  }
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        <div className="space-y-6">
          {currentQuestions.map((question, idx) => {
            const questionIndex = startIndex + idx;
            const options = [question.Answer01, question.Answer02, question.Answer03].filter(a => a);

            return (
              <div
                key={question.id}
                className="bg-white rounded-2xl p-6 shadow-sm"
              >
                <div>
                  <h3 className="text-base font-bold text-gray-800 mb-4 leading-relaxed">
                    <span className="text-orange-500 mr-2">Q{questionIndex + 1}.</span>
                    {question.Question}
                  </h3>
                  <div className="space-y-3">
                    {options.map((option, optionIdx) => (
                      <button
                        key={optionIdx}
                        onClick={() => handleAnswer(question.id, optionIdx)}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 ${answers[question.id] === optionIdx
                          ? 'bg-orange-50 border-orange-200 shadow-sm'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 ${answers[question.id] === optionIdx
                            ? 'border-orange-500 bg-orange-500'
                            : 'border-gray-300'
                            }`}
                        >
                          {answers[question.id] === optionIdx && (
                            <i className="ri-check-line text-white text-xs"></i>
                          )}
                        </div>
                        <span className={`text-sm ${answers[question.id] === optionIdx ? 'text-orange-900 font-medium' : 'text-gray-700'}`}>
                          {option}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between text-xs mb-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-orange-400 rounded-full"></div>
                <span className="text-gray-600">回答済: <strong className="text-gray-800">{answeredCount}</strong></span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-gray-200 rounded-full"></div>
                <span className="text-gray-600">未回答: <strong className="text-gray-800">{Math.max(0, questions.length - answeredCount)}</strong></span>
              </div>
            </div>
            <div className="text-gray-400">
              {questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0}%
            </div>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-orange-400 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${questions.length > 0 ? (answeredCount / questions.length) * 100 : 0}%` }}
            ></div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <div className="flex justify-between gap-3">
            {currentPage > 1 ? (
              <button
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="flex-1 px-4 py-3 bg-white text-gray-600 border border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                前へ
              </button>
            ) : (
              <div className="flex-1"></div>
            )}

            {currentPage < totalPages ? (
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="flex-1 px-4 py-3 bg-[#6B96C3] text-white rounded-xl font-bold hover:bg-[#5A85B2] transition-all whitespace-nowrap cursor-pointer text-sm shadow-sm"
              >
                次へ
                <i className="ri-arrow-right-line ml-1"></i>
              </button>
            ) : (
              <button
                onClick={() => setShowSubmitForm(true)}
                className="flex-1 px-4 py-3 bg-[#F4B183] text-white rounded-xl font-bold hover:bg-[#E5A072] transition-all whitespace-nowrap cursor-pointer text-sm shadow-sm"
              >
                <i className="ri-send-plane-fill mr-1"></i>
                提出
              </button>
            )}
          </div>
        </div>
      </main>

      {showSubmitForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl">
            <form onSubmit={handleSubmit}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-send-line text-xl text-orange-500"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800">模擬試験の提出</h3>
              </div>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                模擬試験を終了して結果を表示します。<br />よろしいですか？
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowSubmitForm(false)}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer text-sm"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-[#F4B183] text-white rounded-xl font-bold hover:bg-[#E5A072] transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer text-sm shadow-sm"
                >
                  {isSubmitting ? '採点中...' : '採点する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <RestrictionModal
        open={isRestrictionOpen}
        message={settings.message}
        actionLabel="学習メニューへ戻る"
        onAction={() => navigate('/learning')}
      />
    </div>
  );
}
